import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { SERVICES } from "@/lib/services";
import { AbsenceBands } from "@/components/AgendaAbsence";
import { AreaCollapseToggle } from "@/components/worker/AreaCollapseToggle";
import { CalendarClock, ChevronRight, Clock, ArrowUpRight } from "lucide-react";

// Palette 3 ufficiale Account Lavoratore (solo per questi nuovi componenti).
const C = { ink: "#22364D", slate: "#557A95", soft: "#A9C7DD", pale: "#EDF5FA", chalk: "#F3F0EB", sand: "#DDD1CF", cipria: "#F0E1E7", cipriaDeep: "#C6A3B4", cipriaBorder: "#E1CFD8", cipriaOn: "#4A3340" };

const hhmmToMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
const pad2 = (n) => String(n).padStart(2, "0");
const slugFromKey = (key) => Object.keys(SERVICES).find((s) => SERVICES[s].key === key) || key;
const svcLabel = (key) => SERVICES[slugFromKey(key)]?.label || key;
const iconFor = (key) => SERVICES[slugFromKey(key)]?.icon || CalendarClock;

export function RegistroSuggeritoDayflow({ reloadKey, collapsed = false, onToggle }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Ricalcolo con orario server ad ogni caricamento/rientro nella Home (nessun polling/timer).
    api.get("/agenda/today").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoaded(true));
  }, [reloadKey]);

  if (!loaded || !data) return null;
  const slots = data.slots || [];
  if (slots.length === 0) return null;   // nessun servizio oggi → nessun blocco (no card vuote)

  const suggested = slots.find((s) => s.id === data.suggested_id) || null;
  const nowMin = data.now_min;
  const nowLabel = `${pad2(Math.floor(nowMin / 60))}:${pad2(nowMin % 60)}`;

  // Indice del primo slot non ancora concluso (fine > adesso): lì va la linea "Ora".
  const nowIndex = (() => {
    for (let i = 0; i < slots.length; i++) {
      const sm = hhmmToMin(slots[i].start_time);
      const em = hhmmToMin(slots[i].end_time);
      const eff = em > sm ? em : 1440;
      if (eff > nowMin) return i;
    }
    return slots.length;
  })();

  const openSuggested = () => {
    if (!suggested) return;
    let url = `/registro/${slugFromKey(suggested.service_type)}/${suggested.entity_id}`;
    const qp = ["open=1"];
    if (suggested.entity_kind) qp.push(`kind=${suggested.entity_kind}`);
    if (suggested.role_or_task) qp.push(`role=${encodeURIComponent(suggested.role_or_task)}`);
    if (suggested.start_time) qp.push(`start=${encodeURIComponent(suggested.start_time)}`);
    if (data.date) qp.push(`date=${encodeURIComponent(data.date)}`);
    url += `?${qp.join("&")}`;
    navigate(url);
  };

  const NowMarker = ({ k }) => (
    <div className="relative mb-2.5 flex items-center gap-1.5" data-testid="dayflow-now-marker" key={k}>
      <div className="absolute -left-[13px] h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: C.ink }} />
      <span className="text-[9px] font-bold uppercase tracking-wide shrink-0" style={{ color: C.ink }}>ora · {nowLabel}</span>
      <div className="flex-1 h-[2px] rounded" style={{ backgroundColor: C.ink }} />
    </div>
  );

  return (
    <div className="mt-4" data-testid="registro-suggerito-dayflow">
      <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <span className="inline-block h-4 w-1.5 rounded-full" style={{ backgroundColor: C.cipriaDeep }} />La mia agenda
        <AreaCollapseToggle testid="toggle-area-agenda" collapsed={collapsed} onToggle={onToggle} />
      </h2>
      {!collapsed && (
      <div className={`grid gap-3 items-start ${suggested ? "grid-cols-2" : "grid-cols-1"}`}>

        {suggested && (
          <div data-testid="registro-suggerito-card" className="min-w-0 rounded-2xl border p-3.5 flex flex-col"
            style={{ backgroundColor: "#FFFFFF", borderColor: C.cipriaBorder, boxShadow: "inset 5px 0 0 0 " + C.cipriaDeep }}>
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC]">
                {(() => { const Ic = iconFor(suggested.service_type); return <Ic className="h-4 w-4" style={{ color: C.ink }} />; })()}
              </div>
              <span className="text-[10px] font-bold tracking-wide rounded-full px-2 py-0.5 truncate" style={{ backgroundColor: "#E1E9F1", color: C.ink }}>SUGGERITO</span>
            </div>
            <p className="font-heading font-bold leading-tight" style={{ color: C.ink }}>
              {svcLabel(suggested.service_type)}{suggested.role_or_task ? ` · ${suggested.role_or_task}` : ""}
            </p>
            <p className="text-sm truncate" style={{ color: C.slate }}>{suggested.titolo}{suggested.sottotitolo ? ` · ${suggested.sottotitolo}` : ""}</p>
            <p className="mt-1.5 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: C.ink }}>
              <Clock className="h-3.5 w-3.5 shrink-0" />{suggested.start_time}–{suggested.end_time}
            </p>
            <p className="text-[11px] mt-1 leading-snug" style={{ color: C.slate }}>Previsto dalla tua Agenda Settimanale</p>
            <button data-testid="registro-suggerito-open" onClick={openSuggested}
              className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 active:scale-[0.99]"
              style={{ backgroundColor: C.ink }}>
              Apri servizio <ChevronRight className="h-4 w-4 shrink-0" />
            </button>
          </div>
        )}

        {/* Dayflow — Oggi */}
        <div data-testid="dayflow-card" className="min-w-0 rounded-2xl border p-3.5" style={{ backgroundColor: "#FFFFFF", borderColor: C.sand }}>
          <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
            <span className="text-[11px] font-bold tracking-wide shrink-0" style={{ color: C.slate }}>OGGI</span>
            <button data-testid="dayflow-see-agenda" onClick={() => navigate("/agenda")}
              className="inline-flex items-center gap-1 text-[11px] font-semibold hover:underline shrink-0 whitespace-nowrap" style={{ color: C.ink }}>
              Agenda <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>

          <AbsenceBands absences={data.absences} />

          <div className="relative pl-4">
            {/* Rail verticale */}
            <div className="absolute left-[6px] top-1 bottom-1 w-[2px] rounded" style={{ backgroundColor: C.sand }} />
            {(() => {
              const rows = [];
              slots.forEach((s, i) => {
                if (i === nowIndex) rows.push(<NowMarker k={`now-${i}`} key={`now-${i}`} />);
                const startMin = hhmmToMin(s.start_time);
                const endMin = hhmmToMin(s.end_time);
                const eff_end = endMin > startMin ? endMin : 1440;
                const future = startMin > nowMin;
                const past = eff_end <= nowMin && s.status !== "in_corso";
                const emphasis = s.is_suggested || s.status === "in_corso" || s.status === "prossimo";
                const isSug = s.is_suggested || s.status === "prossimo";
                // Dayflow: alterna l'intera palette (incluso il cipria) secondo lo stato dello slot.
                const dot = s.status === "in_corso" ? C.ink : isSug ? C.cipriaDeep : emphasis ? C.ink : past ? C.slate : future ? C.soft : C.slate;
                const statusLabel = s.status === "in_corso" ? "In corso" : s.status === "concluso" ? "Concluso"
                  : s.status === "prossimo" || s.is_suggested ? "Prossimo" : future ? "Previsto" : null;
                rows.push(
                  <div key={s.id} className="relative mb-2.5 last:mb-0 min-w-0" data-testid={`dayflow-slot-${s.id}`}
                    style={{ opacity: future ? 0.7 : past ? 0.85 : 1 }}>
                    <div className="absolute -left-4 top-1.5 h-3 w-3 rounded-full border-2 border-white" style={{ backgroundColor: dot }} />
                    <div className="rounded-lg px-2.5 py-1.5 min-w-0" style={{ backgroundColor: emphasis ? (isSug ? C.cipria : C.pale) : "transparent" }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold" style={{ color: C.ink }}>{s.start_time}–{s.end_time}</span>
                        {s.is_substitution && (
                          <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
                            style={{ color: C.cipriaOn, backgroundColor: C.cipria, border: `1px solid ${C.cipriaBorder}` }}>Sostituzione</span>
                        )}
                        {statusLabel && (
                          <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
                            style={{ color: emphasis ? (isSug ? C.cipriaOn : "#fff") : C.slate, backgroundColor: emphasis ? (isSug ? C.cipriaDeep : C.slate) : C.chalk }}>{statusLabel}</span>
                        )}
                      </div>
                      <p className="text-xs font-medium truncate" style={{ color: C.slate }}>
                        {svcLabel(s.service_type)} · {s.titolo}
                      </p>
                    </div>
                  </div>
                );
              });
              if (nowIndex >= slots.length) rows.push(<NowMarker k="now-end" key="now-end" />);
              return rows;
            })()}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
